import React, { useState } from "react";
import Swal from "sweetalert2";
import { updaterNotificationSetting } from "../../services/user.service";
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { getUser, setLocalKey } from "../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";

function NotificationSettings() {
  const { t } = useTranslation();

  const userData = getUser(ROLE.USER) || {};

  const [settings, setSettings] = useState({
    stream_notification: userData?.stream_notification ?? false,
    general_notification: userData?.general_notification ?? false,
  });

  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);

  // Update LS only for notification fields
  const updateLocalStorage = (updatedFields) => {
    const oldData = getUser(ROLE.USER) || {};
    const newData = { ...oldData, ...updatedFields };
    setLocalKey(ROLE.USER, JSON.stringify(newData));
  };

  // On Toggle Click
  const handleToggle = (key, value) => {
    setPendingToggle({ key, value });
    setIsModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingToggle) return;
    const { key, value } = pendingToggle;
    
    setLoading(true);
    setIsModalOpen(false);

    try {
      const payload = {};
      payload[key] = value;

      const res = await updaterNotificationSetting(payload);

      if (res.success) {
        Swal.fire({
          icon: "success",
          text: res.message,
          ...SWAL_SETTINGS,
        });

        const updated = { [key]: value };

        // Update UI
        setSettings((prev) => ({ ...prev, ...updated }));

        // Update LocalStorage
        updateLocalStorage(updated);
      } else {
        Swal.fire({
          icon: "error",
          text: res.message,
          ...SWAL_SETTINGS,
        });
        // Revert UI if needed? (Currently data is only updated on success)
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err.message,
        ...SWAL_SETTINGS,
      });
    } finally {
      setLoading(false);
      setPendingToggle(null);
    }
  };

  return (
    <div className="edit-profile-bg py-10">
      <div className="container">
        <div className="max-w-[797px] mx-auto">
          <h1 className="headings-h1 text-zinc-950 border-b border-gray-200 pb-[15px] mb-[30px]">{t("notification_settings")}</h1>

          <div className="bg-white border-1 border-zinc-100 p-[15px] md:p-[30px] lg:p-[50px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] rounded-[10px]">
            <ul className="space-y-[15px]">
              {/* Stream Notification */}
              <li className="flex items-center justify-between gap-[10px] flex-wrap border-1 border-zinc-100 p-[15px] rounded-[10px]">
                <div className="flex flex-col gap-[10px]">
                  <h3 className="headings-h5 text-zinc-950">{t("live_stream_alerts")}</h3>
                  <p className="body-lg text-zinc-500">{t("get_notified_when_streams_go_live")}</p>
                </div>

                <input type="checkbox" className="toggle toggle-accent" checked={settings.stream_notification} disabled={loading} onChange={(e) => handleToggle("stream_notification", e.target.checked)} />
              </li>

              {/* General Notification */}
              {/* <li className="flex items-center justify-between gap-[10px] flex-wrap border-1 border-zinc-100 p-[15px] rounded-[10px]">
                <div className="flex flex-col gap-[10px]">
                  <h3 className="headings-h5 text-zinc-950">{t("general_alerts")}</h3>
                  <p className="body-lg text-zinc-500">{t("receiving_alerts_based_on_user_activity")}</p>
                </div>

                <input type="checkbox" className="toggle toggle-accent" checked={settings.general_notification} disabled={loading} onChange={(e) => handleToggle("general_notification", e.target.checked)} />
              </li> */}
            </ul>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      {isModalOpen && (
        <dialog open className="modal p-[15px] flex items-center justify-center bg-black/60 z-[9999]">
          <div className="modal-box max-w-[515px] !w-full bg-white p-[30px] rounded-[20px] shadow-2xl relative z-[10000]">
            <div className="text-center flex items-center justify-center flex-col gap-[20px]">
              <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mb-2">
                <img src="/images/bell1.png" alt="Notification" className="w-10 h-10 object-contain" />
              </div>
              <div className="flex flex-col gap-[10px]">
                <h1 className="headings-h1 text-zinc-800">{t("msg_are_you_sure")}</h1>
                <p className="body-lg text-gray-500">
                  {pendingToggle?.value 
                    ? t("msg_want_to_enable") 
                    : t("msg_want_to_disable")}
                </p>
              </div>
              <div className="flex items-center gap-[15px] w-full mt-[15px]">
                <button
                  className="btn btn-outline flex-1 border-red-500 text-red-500 hover:bg-red-50 font-bold h-[50px]"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading ? "Updating..." : t("btn_yes")}
                </button>
                <button
                  className="btn btn-pri flex-1 bg-red-500 border-red-500 text-white hover:bg-red-600 font-bold h-[50px]"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                >
                  {t("btn_no")}
                </button>
              </div>
            </div>
          </div>
          <form method="dialog" className="fixed inset-0 z-[9998]" onClick={() => setIsModalOpen(false)}>
            <button 
              className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-white subtitle-2 flex items-center gap-[10px] px-[25px] py-[12px] rounded-full shadow-2xl border border-gray-100 hover:bg-gray-50 transition-all font-bold text-zinc-800"
            >
              <img src="/images/close.svg" alt="close" className="w-4 h-4" />
              <span>{t("common.close")}</span>
            </button>
          </form>
        </dialog>
      )}
    </div>
  );
}

export default NotificationSettings;
